/**
 * 토스페이먼츠 결제창 (BIZ-69 스파이크 결정 · BIZ-78).
 *
 * v1 SDK를 스크립트 태그로 지연 로드하고(다음 우편번호와 같은 패턴), requestPayment('카드')로
 * 결제창을 띄운다. 결제가 승인되면 토스가 successUrl로 리다이렉트하며 paymentKey·orderId(=우리
 * orderKey)·amount 쿼리를 붙여준다 — 그걸 /api/payments/confirm으로 보내 서버가 확정한다.
 * 클라이언트 키는 VITE_TOSS_CLIENT_KEY(공개 가능한 키). 시크릿 키는 서버 전용이다.
 */

interface TossPaymentsInstance {
  requestPayment: (
    method: string,
    opts: {
      amount: number
      orderId: string
      orderName: string
      successUrl: string
      failUrl: string
    },
  ) => Promise<void>
}
declare global {
  interface Window {
    TossPayments?: (clientKey: string) => TossPaymentsInstance
  }
}

const TOSS_SDK_SRC = 'https://js.tosspayments.com/v1/payment'
let tossLoader: Promise<void> | null = null
function loadTossSdk(): Promise<void> {
  if (window.TossPayments) return Promise.resolve()
  if (tossLoader) return tossLoader
  tossLoader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TOSS_SDK_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      tossLoader = null // 실패는 캐시하지 않는다(다음 시도 재로드).
      reject(new Error('결제 모듈을 불러오지 못했습니다'))
    }
    document.head.appendChild(script)
  })
  return tossLoader
}

/** 사용자가 결제창을 그냥 닫은 경우(에러 아님 — 조용히 돌아온다). */
export function isTossUserCancel(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: string }).code === 'PAY_PROCESS_CANCELED'
}

/**
 * 카드 결제창을 띄운다. 성공하면 페이지가 successUrl로 떠나므로 이 함수는 돌아오지 않는다.
 * 사용자가 닫으면 PAY_PROCESS_CANCELED로 reject된다(isTossUserCancel로 구분).
 */
export async function startTossPayment(opts: {
  orderId: number
  orderKey: string
  amount: number
  orderName: string
}): Promise<void> {
  const clientKey = import.meta.env['VITE_TOSS_CLIENT_KEY'] as string | undefined
  if (!clientKey) throw new Error('결제 키가 설정되지 않았습니다 (VITE_TOSS_CLIENT_KEY)')
  await loadTossSdk()
  const toss = window.TossPayments!(clientKey)
  const base = window.location.origin
  await toss.requestPayment('카드', {
    amount: opts.amount,
    orderId: opts.orderKey,
    orderName: opts.orderName,
    // oid = 우리 내부 주문 id. 돌아와서 상세로 돌려보낼 때 쓴다(토스 파라미터와 별개).
    successUrl: `${base}/payments/success?oid=${opts.orderId}`,
    failUrl: `${base}/payments/fail?oid=${opts.orderId}`,
  })
}
